import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getMe, logout as apiLogout, getAppleConfig, appleStartUrl } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appleEnabled, setAppleEnabled] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const u = await getMe();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAppleConfig().then(setAppleEnabled);
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check — AuthCallback exchanges the
    // session_id and establishes the session cookie first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Return the user to the page they logged in from (e.g. "/" for the paywall, "/dashboard" for admin).
    const redirectUrl = window.location.origin + (window.location.pathname || "/");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginApple = () => {
    window.location.href = appleStartUrl;
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      // ignore — clear locally regardless
      console.warn("Server logout failed; clearing session locally.", e);
    }
    setUser(null);
  };

  const hasActivePass = !!(user && user.has_active_pass);

  return (
    <AuthContext.Provider value={{ user, loading, hasActivePass, appleEnabled, checkAuth, login, loginApple, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
